import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Upload } from 'lucide-react';
import apiClient from '../../../services/apiClient';

interface OperatingHour {
  day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

interface Gym {
  _id: string;
  name: string;
  code: string;
  phone: string;
  address: string;
  city: string;
  photoUrl: string;
  timezone: string;
  description?: string;
  facilities?: string[];
  operatingHours?: OperatingHour[];
  coverPhoto?: string;
}

interface GymFormProps {
  gym?: Gym | null;
  onClose: () => void;
  onSuccess: () => void;
}

const GymForm: React.FC<GymFormProps> = ({ gym, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    phone: '',
    address: '',
    city: '',
    timezone: 'Africa/Addis_Ababa',
    description: '',
    facilities: [] as string[],
    operatingHours: [] as OperatingHour[],
    coverPhoto: '',
  });

  const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string>('');
  const [facilityInput, setFacilityInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (gym) {
      setFormData({
        name: gym.name,
        code: gym.code,
        phone: gym.phone,
        address: gym.address,
        city: gym.city,
        timezone: gym.timezone,
        description: gym.description || '',
        facilities: gym.facilities || [],
        operatingHours: gym.operatingHours || [],
        coverPhoto: gym.coverPhoto || '',
      });
      if (gym.coverPhoto) {
        setCoverPhotoPreview(gym.coverPhoto);
      }
    }
  }, [gym]);

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      if (gym) {
        return await apiClient.patch(`/gyms/${gym._id}`, data);
      } else {
        return await apiClient.post('/gyms', data);
      }
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error saving gym';
      setErrors({ submit: message });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleCoverPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddFacility = () => {
    if (facilityInput.trim()) {
      setFormData(prev => ({
        ...prev,
        facilities: [...prev.facilities, facilityInput.trim()],
      }));
      setFacilityInput('');
    }
  };

  const handleRemoveFacility = (index: number) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.filter((_, i) => i !== index),
    }));
  };

  const handleOperatingHourChange = (day: string, field: 'openTime' | 'closeTime' | 'isClosed', value: any) => {
    setFormData(prev => {
      const existingIndex = prev.operatingHours.findIndex((oh: OperatingHour) => oh.day === day);
      const updatedHours = [...prev.operatingHours];

      if (existingIndex >= 0) {
        updatedHours[existingIndex] = {
          ...updatedHours[existingIndex],
          [field]: value,
        };
      } else {
        updatedHours.push({
          day: day as any,
          openTime: field === 'openTime' ? value : '06:00',
          closeTime: field === 'closeTime' ? value : '22:00',
          isClosed: field === 'isClosed' ? value : false,
        });
      }

      return {
        ...prev,
        operatingHours: updatedHours,
      };
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // For new gyms, convert file to base64 and include in formData
    // For existing gyms with new file, upload separately
    if (coverPhotoFile && gym) {
      // Editing existing gym - upload file first
      const uploadFormData = new FormData();
      uploadFormData.append('file', coverPhotoFile);

      apiClient
        .post(`/gyms/${gym._id}/upload-photo`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
          // Backend returns coverPhoto in the response
          let coverPhotoUrl = response.data?.coverPhoto || response.data?.photoUrl || response.data?.data?.coverPhoto;
          
          // If it's a relative path, prepend the base URL (without /api/v1)
          if (coverPhotoUrl && coverPhotoUrl.startsWith('/uploads')) {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
            coverPhotoUrl = baseUrl.replace('/api/v1', '') + coverPhotoUrl;
          }
          
          mutation.mutate({
            ...formData,
            coverPhoto: coverPhotoUrl,
          });
        })
        .catch((error) => {
          setErrors({ submit: 'Failed to upload cover photo' });
        });
    } else if (coverPhotoFile && !gym) {
      // Creating new gym - convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        mutation.mutate({
          ...formData,
          coverPhoto: reader.result as string,
        });
      };
      reader.readAsDataURL(coverPhotoFile);
    } else {
      // No file selected
      mutation.mutate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-800 sticky top-0 bg-gray-900">
          <h2 className="text-2xl font-bold text-white">
            {gym ? 'Edit Gym' : 'Create New Gym'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-500 bg-opacity-10 border border-red-500 text-red-400 p-3 rounded">
              {errors.submit}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Basic Information</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Gym Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  placeholder="Enter gym name"
                />
                {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Code
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  placeholder="e.g., 1234"
                  disabled={!!gym}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  placeholder="Enter phone number"
                />
                {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  placeholder="Enter address"
                />
                {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  placeholder="Enter city"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Timezone
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                >
                  <option value="Africa/Addis_Ababa">Africa/Addis_Ababa</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
              placeholder="Enter gym description"
              rows={3}
            />
          </div>

          {/* Facilities */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Facilities
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={facilityInput}
                onChange={(e) => setFacilityInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFacility())}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                placeholder="Add facility and press Enter"
              />
              <button
                type="button"
                onClick={handleAddFacility}
                className="bg-[#23F47C] text-gray-900 px-4 py-2 rounded font-semibold hover:bg-opacity-90"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.facilities.map((facility, index) => (
                <div
                  key={index}
                  className="bg-[#23F47C] bg-opacity-10 text-[#23F47C] px-3 py-1 rounded-full flex items-center gap-2"
                >
                  <span>{facility}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFacility(index)}
                    className="hover:text-red-400"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Operating Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Operating Hours
            </label>
            <div className="space-y-3">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                const dayHours = formData.operatingHours.find((oh: OperatingHour) => oh.day === day);
                return (
                  <div key={day} className="bg-gray-800 border border-gray-700 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{day}</span>
                      <label className="flex items-center gap-2 text-gray-400">
                        <input
                          type="checkbox"
                          checked={dayHours?.isClosed || false}
                          onChange={(e) => handleOperatingHourChange(day, 'isClosed', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Closed</span>
                      </label>
                    </div>
                    {!dayHours?.isClosed && (
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-400 mb-1">Open Time</label>
                          <input
                            type="time"
                            value={dayHours?.openTime || '06:00'}
                            onChange={(e) => handleOperatingHourChange(day, 'openTime', e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white focus:outline-none focus:border-[#23F47C]"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-400 mb-1">Close Time</label>
                          <input
                            type="time"
                            value={dayHours?.closeTime || '22:00'}
                            onChange={(e) => handleOperatingHourChange(day, 'closeTime', e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white focus:outline-none focus:border-[#23F47C]"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cover Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Cover Photo
            </label>
            
            {/* Preview */}
            {coverPhotoPreview && (
              <div className="mb-4 rounded-lg overflow-hidden h-40 bg-gray-800">
                <img
                  src={coverPhotoPreview}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* File Upload */}
            <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-[#23F47C] transition-colors">
              <div className="flex items-center space-x-2">
                <Upload size={20} className="text-gray-400" />
                <span className="text-gray-400">
                  {coverPhotoFile ? coverPhotoFile.name : 'Click to upload or drag and drop'}
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverPhotoChange}
                className="hidden"
              />
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 text-white px-4 py-2 rounded font-semibold hover:bg-gray-700 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 bg-[#23F47C] text-gray-900 px-4 py-2 rounded font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving...' : gym ? 'Update Gym' : 'Create Gym'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GymForm;
